import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  createTeamExternalLeagueLink,
  applyExternalLeagueProfile,
  AppliedTeamProfile,
  deleteTeamExternalLeagueLink,
  ExternalLeagueLink,
  ExternalLeagueProvider,
  ExternalLeagueSyncResult,
  ExternalTeamSearchItem,
  ExternalAddressCandidate,
  ExternalProfileCandidate,
  ExternalEventSuppression,
  getExternalLeagueAddressCandidates,
  getTeamExternalLeagueLinks,
  getExternalEventSuppressions,
  restoreExternalEventSuppression,
  searchExternalLeagueTeams,
  syncAllTeamExternalLeagueLinks,
  syncTeamExternalLeagueLink,
} from "src/api/externalLeagueTeams";
import { CheckboxControl } from "src/components/CheckboxControl";
import { ExternalLeagueBadge, externalLeagueProviderLabel } from "src/components/ExternalLeagueBadge";
import { TeamContactItem } from "src/types/teams";
import { LoadingIndicator } from "src/components/LoadingIndicator";
import { cardStyle, inputStyle } from "src/pages/TeamsPage/components/styles";

interface TeamExternalLeagueSettingsProps {
  teamId: string;
  teamName: string;
  teamAvatarUrl: string | null;
  teamCoverImageUrl: string | null;
  teamDescription?: string;
  teamPhones?: TeamContactItem[];
  teamLinks?: TeamContactItem[];
  teamAddresses?: TeamContactItem[];
  onTeamProfileApplied: (profile: AppliedTeamProfile) => void | Promise<void>;
}

type BusyOperation = "search" | "add" | "primary" | "sync" | "sync-all" | "remove" | "apply" | "restore";

const providerOptions = [
  { value: ExternalLeagueProvider.Spbhl, label: "СПбХЛ" },
];

const normalizeValue = (value: string) => value.trim().toLocaleLowerCase("ru-RU").replace(/\s+/g, " ").replace(/\/$/, "");
const toggleCandidate = (id: string, selected: string[], setSelected: (value: string[]) => void) =>
  setSelected(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);

function CandidateGroup({ title, candidates, selected, setSelected, existing, disabled }: {
  title: string;
  candidates: ExternalProfileCandidate[];
  selected: string[];
  setSelected: (value: string[]) => void;
  existing: TeamContactItem[];
  disabled: boolean;
}) {
  return <div style={{ display: "grid", gap: 7 }}><strong>{title}</strong>{candidates.map((candidate) => {
    const exists = existing.some((item) => normalizeValue(item.value) === normalizeValue(candidate.value));
    return <CheckboxControl
      key={candidate.candidateId}
      checked={selected.includes(candidate.candidateId)}
      disabled={disabled || exists}
      onChange={() => toggleCandidate(candidate.candidateId, selected, setSelected)}
      label={candidate.label || candidate.value}
      description={`${candidate.label ? `${candidate.value}${exists ? " · " : ""}` : ""}${exists ? "Уже добавлено" : ""}` || undefined}
    />;
  })}</div>;
}

const buttonStyle = {
  border: 0,
  borderRadius: 8,
  padding: "10px 12px",
  background: "var(--hp-primary)",
  color: "white",
  fontWeight: 800,
  cursor: "pointer",
  flex: "1 1 150px",
} as const;

const secondaryButtonStyle = {
  ...buttonStyle,
  background: "var(--hp-primary-soft)",
  color: "var(--hp-primary)",
};

const formatTimestamp = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const SyncSummary = ({ result }: { result: ExternalLeagueSyncResult }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))", gap: 6, fontSize: 13 }}>
    <span>Получено: <strong>{result.receivedCount}</strong></span>
    <span>Добавлено: <strong>{result.createdCount}</strong></span>
    <span>Обновлено: <strong>{result.updatedCount}</strong></span>
    <span>Без изменений: <strong>{result.unchangedCount}</strong></span>
  </div>
);

export function TeamExternalLeagueSettings({ teamId, teamName: _teamName, teamAvatarUrl, teamCoverImageUrl, teamDescription = "", teamPhones = [], teamLinks = [], teamAddresses = [], onTeamProfileApplied }: TeamExternalLeagueSettingsProps) {
  const [provider, setProvider] = useState(ExternalLeagueProvider.Spbhl);
  const [links, setLinks] = useState<ExternalLeagueLink[]>([]);
  const [suppressions, setSuppressions] = useState<ExternalEventSuppression[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<BusyOperation | null>(null);
  const [title, setTitle] = useState("");
  const [results, setResults] = useState<ExternalTeamSearchItem[]>([]);
  const [summaries, setSummaries] = useState<Record<string, ExternalLeagueSyncResult>>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [applyLinkId, setApplyLinkId] = useState<string | null>(null);
  const [applyName, setApplyName] = useState(false);
  const [applyLogo, setApplyLogo] = useState(false);
  const [applyCover, setApplyCover] = useState(false);
  const [applyDescription, setApplyDescription] = useState(false);
  const [selectedPhones, setSelectedPhones] = useState<string[]>([]);
  const [selectedWebsites, setSelectedWebsites] = useState<string[]>([]);
  const [selectedAddresses, setSelectedAddresses] = useState<string[]>([]);
  const [addressCandidates, setAddressCandidates] = useState<ExternalAddressCandidate[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const operationLocked = useRef(false);
  const operationGeneration = useRef(0);
  const loadGeneration = useRef(0);
  const searchGeneration = useRef(0);
  const activeTeamId = useRef(teamId);
  const mounted = useRef(true);

  const loadLinks = useCallback(async () => {
    const generation = ++loadGeneration.current;
    const requestedTeamId = teamId;
    setLoading(true);
    setError(null);
    try {
      const [nextLinks, nextSuppressions] = await Promise.all([
        getTeamExternalLeagueLinks(teamId),
        getExternalEventSuppressions(teamId),
      ]);
      if (mounted.current && generation === loadGeneration.current && requestedTeamId === activeTeamId.current) {
        setLinks(nextLinks);
        setSuppressions(nextSuppressions);
      }
    } catch (requestError) {
      if (mounted.current && generation === loadGeneration.current && requestedTeamId === activeTeamId.current) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось загрузить данные лиги.");
      }
    } finally {
      if (mounted.current && generation === loadGeneration.current && requestedTeamId === activeTeamId.current) {
        setLoading(false);
      }
    }
  }, [teamId]);

  useEffect(() => {
    mounted.current = true;
    activeTeamId.current = teamId;
    operationLocked.current = false;
    operationGeneration.current += 1;
    loadGeneration.current += 1;
    searchGeneration.current += 1;
    setLinks([]);
    setSuppressions([]);
    setBusy(null);
    setTitle("");
    setResults([]);
    setSummaries({});
    setError(null);
    setMessage(null);
    setApplyLinkId(null);
    void loadLinks();
    return () => {
      mounted.current = false;
      operationGeneration.current += 1;
      loadGeneration.current += 1;
      searchGeneration.current += 1;
    };
  }, [loadLinks, teamId]);

  const beginOperation = (operation: BusyOperation): number | null => {
    if (operationLocked.current) return null;
    operationLocked.current = true;
    setBusy(operation);
    setError(null);
    setMessage(null);
    return ++operationGeneration.current;
  };

  const isCurrentOperation = (generation: number, requestedTeamId: string) =>
    mounted.current &&
    generation === operationGeneration.current &&
    requestedTeamId === activeTeamId.current;

  const finishOperation = (generation: number, requestedTeamId: string) => {
    if (!isCurrentOperation(generation, requestedTeamId)) return;
    operationLocked.current = false;
    setBusy(null);
  };

  const handleSearch = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = title.trim();
    if (normalized.length < 2 || normalized.length > 100) {
      setError("Введите от 2 до 100 символов.");
      return;
    }
    const requestedTeamId = teamId;
    const operation = beginOperation("search");
    if (operation === null) return;
    const search = ++searchGeneration.current;
    try {
      const found = await searchExternalLeagueTeams(provider, normalized);
      if (isCurrentOperation(operation, requestedTeamId) && search === searchGeneration.current) setResults(found);
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId) && search === searchGeneration.current) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось найти команду.");
      }
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  const handleAdd = async (item: ExternalTeamSearchItem) => {
    const requestedTeamId = teamId;
    const operation = beginOperation("add");
    if (operation === null) return;
    try {
      const link = await createTeamExternalLeagueLink(teamId, {
        provider: item.provider,
        externalTeamId: item.externalTeamId,
        isPrimary: false,
      });
      if (!isCurrentOperation(operation, requestedTeamId)) return;
      setLinks((current) => [...current.filter((value) => value.id !== link.id), link]);
      setResults([]);
      setTitle("");
      setMessage(`Команда «${link.externalTeamName}» добавлена.`);
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId)) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось добавить команду.");
      }
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  const handleMakePrimary = async (link: ExternalLeagueLink) => {
    const requestedTeamId = teamId;
    const operation = beginOperation("primary");
    if (operation === null) return;
    try {
      const primary = await createTeamExternalLeagueLink(teamId, {
        provider: link.provider,
        externalTeamId: link.externalTeamId,
        isPrimary: true,
      });
      if (!isCurrentOperation(operation, requestedTeamId)) return;
      setLinks((current) => current.map((value) => value.provider === primary.provider
        ? { ...value, isPrimary: value.id === primary.id, ...(value.id === primary.id ? primary : {}) }
        : value));
      setMessage(`«${primary.externalTeamName}» теперь основная команда.`);
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId)) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось выбрать основную команду.");
      }
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  const handleSync = async (link: ExternalLeagueLink) => {
    const requestedTeamId = teamId;
    const operation = beginOperation("sync");
    if (operation === null) return;
    try {
      const result = await syncTeamExternalLeagueLink(teamId, link.id);
      if (!isCurrentOperation(operation, requestedTeamId)) return;
      setSummaries((current) => ({ ...current, [link.id]: result }));
      setLinks((current) => current.map((value) => value.id === link.id
        ? { ...value, lastSyncAttemptAt: result.syncedAt, lastSuccessfulSyncAt: result.syncedAt }
        : value));
      setMessage(`Данные «${link.externalTeamName}» обновлены.`);
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId)) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось обновить данные.");
      }
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  const handleSyncAll = async () => {
    const requestedTeamId = teamId;
    const operation = beginOperation("sync-all");
    if (operation === null) return;
    try {
      const syncResults = await syncAllTeamExternalLeagueLinks(teamId);
      if (!isCurrentOperation(operation, requestedTeamId)) return;
      const nextSummaries = Object.fromEntries(syncResults.map((result) => [result.linkId, result]));
      setSummaries((current) => ({ ...current, ...nextSummaries }));
      setLinks((current) => current.map((link) => {
        const result = nextSummaries[link.id];
        return result ? { ...link, lastSyncAttemptAt: result.syncedAt, lastSuccessfulSyncAt: result.syncedAt } : link;
      }));
      setMessage("Данные всех команд обновлены.");
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId)) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось обновить данные.");
      }
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  const handleRemove = async (link: ExternalLeagueLink) => {
    if (!window.confirm(`Убрать команду «${link.externalTeamName}»?\n\nКоманда будет убрана из связи с сайтом лиги. Уже добавленные матчи останутся в HockeyPlanner.`)) return;
    const requestedTeamId = teamId;
    const operation = beginOperation("remove");
    if (operation === null) return;
    try {
      await deleteTeamExternalLeagueLink(teamId, link.id);
      if (!isCurrentOperation(operation, requestedTeamId)) return;
      setLinks((current) => current.filter((value) => value.id !== link.id));
      setSummaries((current) => {
        const next = { ...current };
        delete next[link.id];
        return next;
      });
      setMessage(`Команда «${link.externalTeamName}» убрана.`);
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId)) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось убрать команду.");
      }
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  const handleRestoreSuppression = async (suppression: ExternalEventSuppression) => {
    const requestedTeamId = teamId;
    const operation = beginOperation("restore");
    if (operation === null) return;
    try {
      await restoreExternalEventSuppression(teamId, suppression.id);
      if (!isCurrentOperation(operation, requestedTeamId)) return;
      setSuppressions(current => current.filter(item => item.id !== suppression.id));
      setMessage("Мероприятие снова будет добавлено при следующем обновлении лиги.");
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId)) setError(requestError instanceof Error ? requestError.message : "Не удалось вернуть мероприятие.");
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  const openProfileImport = async (link: ExternalLeagueLink) => {
    const requestedTeamId = teamId;
    const generation = operationGeneration.current;
    setApplyLinkId(link.id);
    setApplyName(false);
    setApplyLogo(false);
    setApplyCover(false);
    setApplyDescription(false);
    setSelectedPhones([]);
    setSelectedWebsites([]);
    setSelectedAddresses([]);
    setAddressLoading(true);
    try {
      const candidates = await getExternalLeagueAddressCandidates(teamId);
      if (mounted.current && generation === operationGeneration.current && requestedTeamId === activeTeamId.current) setAddressCandidates(candidates);
    } catch (requestError) {
      if (mounted.current && generation === operationGeneration.current && requestedTeamId === activeTeamId.current) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось загрузить адреса из матчей.");
      }
    } finally {
      if (mounted.current && generation === operationGeneration.current && requestedTeamId === activeTeamId.current) setAddressLoading(false);
    }
  };

  const handleApplyProfile = async (link: ExternalLeagueLink) => {
    if (!applyName && !applyLogo && !applyCover && !applyDescription && selectedPhones.length === 0 && selectedWebsites.length === 0 && selectedAddresses.length === 0) {
      setError("Выберите данные, которые нужно добавить в профиль.");
      return;
    }
    if ((applyLogo && teamAvatarUrl) || (applyCover && teamCoverImageUrl)) {
      if (!window.confirm("Выбранные изображения заменят текущие изображения профиля команды. Продолжить?")) return;
    }
    if (applyDescription && teamDescription && !window.confirm("В описание команды будет добавлена информация с сайта лиги. Ваш текущий текст сохранится.")) return;
    const requestedTeamId = teamId;
    const operation = beginOperation("apply");
    if (operation === null) return;
    try {
      const applied = await applyExternalLeagueProfile(teamId, link.id, {
        useName: applyName,
        useLogo: applyLogo,
        useCover: applyCover,
        useDescriptionMetadata: applyDescription,
        selectedPhoneCandidateIds: selectedPhones,
        selectedWebsiteCandidateIds: selectedWebsites,
        selectedAddressCandidateIds: selectedAddresses,
      });
      if (!isCurrentOperation(operation, requestedTeamId)) return;
      await onTeamProfileApplied(applied);
      setApplyLinkId(null);
      setMessage(`Данные «${link.externalTeamName}» добавлены в профиль.`);
    } catch (requestError) {
      if (isCurrentOperation(operation, requestedTeamId)) {
        setError(requestError instanceof Error ? requestError.message : "Не удалось добавить данные в профиль.");
      }
    } finally {
      finishOperation(operation, requestedTeamId);
    }
  };

  return (
    <section style={{ ...cardStyle, marginTop: 14, display: "grid", gap: 14 }}>
      <div>
        <h2 style={{ margin: "0 0 5px", fontSize: 20, color: "var(--hp-text-strong)" }}>Лига и официальный профиль</h2>
        <p style={{ margin: 0, color: "var(--hp-muted)", fontSize: 14, lineHeight: 1.45 }}>
          Добавьте команды с сайта лиги, чтобы получать расписание, переносы и результаты. Здесь можно обновить данные вручную.
        </p>
      </div>

      <label style={{ display: "grid", gap: 6, color: "var(--hp-muted)", fontSize: 13, fontWeight: 800 }}>
        Лига
        <select
          aria-label="Лига"
          value={provider}
          onChange={(event) => {
            setProvider(Number(event.target.value) as ExternalLeagueProvider);
            setResults([]);
            setTitle("");
          }}
          disabled={busy !== null}
          style={inputStyle}
        >
          {providerOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>

      {error && <div role="alert" style={{ padding: 10, borderRadius: 8, background: "var(--hp-danger-soft)", color: "var(--hp-danger)" }}>{error}</div>}
      {message && <div role="status" style={{ padding: 10, borderRadius: 8, background: "var(--hp-success-soft)", color: "var(--hp-success)" }}>{message}</div>}

      {loading && links.length === 0 ? <LoadingIndicator text="Загружаем данные лиги..." /> : (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 17, color: "var(--hp-heading)" }}>Добавленные команды</h3>
            {links.length > 0 && (
              <button type="button" onClick={() => void handleSyncAll()} disabled={busy !== null} style={{ ...secondaryButtonStyle, flex: "0 1 auto" }}>
                {busy === "sync-all" ? "Обновляем..." : "Обновить все данные"}
              </button>
            )}
          </div>

          {links.length === 0 && <div style={{ color: "var(--hp-muted)", fontSize: 14 }}>Команды с сайта лиги пока не добавлены.</div>}
          <div style={{ display: "grid", gap: 10 }}>
            {links.map((link) => {
              const leagueName = externalLeagueProviderLabel(link.provider);
              return <article key={link.id} data-testid={`external-link-${link.id}`} style={{ border: "1px solid var(--hp-border)", borderRadius: 8, padding: 12, display: "grid", gap: 10, minWidth: 0 }}>
                <div style={{ display: "grid", gridTemplateColumns: link.logoUrl ? "52px minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 12, alignItems: "center" }}>
                  {link.logoUrl && <img src={link.logoUrl} alt="" width={52} height={52} style={{ objectFit: "contain", maxWidth: "100%" }} />}
                  <div style={{ minWidth: 0, display: "grid", gap: 4 }}>
                    <strong style={{ color: "var(--hp-heading)", overflowWrap: "anywhere" }}>{link.externalTeamName}</strong>
                    <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>
                      <ExternalLeagueBadge provider={link.provider} division={link.divisionName} />
                    </span>
                    {(link.city || link.country) && <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>{[link.city, link.country].filter(Boolean).join(", ")}</span>}
                    {link.isPrimary && <span style={{ color: "var(--hp-success)", fontSize: 13, fontWeight: 800 }}>Основная команда</span>}
                    <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>
                      {link.lastSuccessfulSyncAt ? `Данные обновлены: ${formatTimestamp(link.lastSuccessfulSyncAt)}` : "Данные ещё не обновлялись"}
                    </span>
                  </div>
                </div>
                {summaries[link.id] && <SyncSummary result={summaries[link.id]} />}
                {applyLinkId === link.id && (
                  <div style={{ display: "grid", gap: 9, padding: 10, borderRadius: 8, background: "var(--hp-surface-soft)" }}>
                    {link.coverUrl && <img src={link.coverUrl} alt="Обложка с сайта лиги" onError={(event) => { event.currentTarget.style.display = "none"; }} style={{ width: "100%", maxHeight: 140, objectFit: "cover", borderRadius: 6, overflowWrap: "anywhere" }} />}
                    {link.logoUrl && <img src={link.logoUrl} alt="Логотип с сайта лиги" onError={(event) => { event.currentTarget.style.display = "none"; }} width={64} height={64} style={{ objectFit: "contain" }} />}
                    <strong style={{ color: "var(--hp-heading)" }}>Добавить данные {leagueName} в профиль команды</strong>
                    <span style={{ color: "var(--hp-muted)", fontSize: 14, lineHeight: 1.45 }}>
                      Выберите, какие данные с официальной страницы «{link.externalTeamName}» добавить в профиль вашей команды.
                    </span>
                    <div style={{ border: "1px solid var(--hp-info-border)", borderRadius: 8, padding: 10, background: "var(--hp-info-soft)", color: "var(--hp-info)", fontSize: 13, lineHeight: 1.45 }}>
                      Данные получены автоматически с сайта лиги. В них могут быть неточности — перепроверьте информацию перед добавлением в профиль.
                    </div>
                    <div style={{ display: "grid", gap: 8 }}>
                      <strong>Профиль команды</strong>
                      <CheckboxControl checked={applyName} onChange={setApplyName} label={`Название: ${link.externalTeamName}`} disabled={busy !== null} />
                      {link.logoUrl && <CheckboxControl checked={applyLogo} onChange={setApplyLogo} label="Логотип" disabled={busy !== null} />}
                      {link.coverUrl && <CheckboxControl checked={applyCover} onChange={setApplyCover} label="Обложка" disabled={busy !== null} />}
                    </div>
                    {(link.foundedYear || link.coachName || link.administratorName) && <div style={{ display: "grid", gap: 6 }}>
                      <strong>Информация</strong>
                      <CheckboxControl checked={applyDescription} onChange={setApplyDescription} disabled={busy !== null} label="Информация о команде" description={[
                        link.foundedYear && `Год создания: ${link.foundedYear}`,
                        link.coachName && `Тренер: ${link.coachName}`,
                        link.administratorName && `Администратор: ${link.administratorName}`,
                      ].filter(Boolean).join("; ")} />
                    </div>}
                    {(link.phoneCandidates ?? []).length > 0 && <CandidateGroup title="Телефоны" candidates={link.phoneCandidates ?? []} selected={selectedPhones} setSelected={setSelectedPhones} existing={teamPhones} disabled={busy !== null} />}
                    {(link.websiteCandidates ?? []).length > 0 && <CandidateGroup title="Ссылки" candidates={link.websiteCandidates ?? []} selected={selectedWebsites} setSelected={setSelectedWebsites} existing={teamLinks} disabled={busy !== null} />}
                    <div style={{ display: "grid", gap: 7 }}>
                      <strong>Адреса из матчей</strong>
                      <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>Мы нашли эти адреса в матчах команды. Выберите подходящие и проверьте их перед добавлением.</span>
                      {addressLoading && <span>Загружаем адреса...</span>}
                      {addressCandidates.map((candidate) => {
                        const exists = teamAddresses.some((item) => normalizeValue(item.value) === normalizeValue(candidate.address));
                        return <CheckboxControl key={candidate.candidateId} checked={selectedAddresses.includes(candidate.candidateId)} disabled={busy !== null || exists} onChange={() => toggleCandidate(candidate.candidateId, selectedAddresses, setSelectedAddresses)} label={candidate.venueName || candidate.address} description={`${candidate.address} · ${candidate.matchCount} матчей${exists ? " · Уже добавлен" : ""}`} />;
                      })}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      <button type="button" onClick={() => void handleApplyProfile(link)} disabled={busy !== null} style={buttonStyle}>{busy === "apply" ? "Добавляем..." : "Добавить выбранное в профиль"}</button>
                      <button type="button" onClick={() => setApplyLinkId(null)} disabled={busy !== null} style={secondaryButtonStyle}>Отмена</button>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {link.profileUrl && <a href={link.profileUrl} target="_blank" rel="noopener noreferrer" style={{ ...secondaryButtonStyle, textAlign: "center", textDecoration: "none", boxSizing: "border-box" }}>Открыть на сайте лиги</a>}
                  <button type="button" onClick={() => void handleSync(link)} disabled={busy !== null} style={buttonStyle}>Обновить данные</button>
                  {!link.isPrimary && <button type="button" onClick={() => void handleMakePrimary(link)} disabled={busy !== null} style={secondaryButtonStyle}>Сделать основной командой</button>}
                  <button type="button" onClick={() => void openProfileImport(link)} disabled={busy !== null} style={secondaryButtonStyle}>Добавить данные с {leagueName}</button>
                  <button type="button" onClick={() => void handleRemove(link)} disabled={busy !== null} style={{ ...secondaryButtonStyle, background: "var(--hp-danger-soft)", color: "var(--hp-danger)" }}>Убрать команду</button>
                </div>
              </article>;
            })}
          </div>
        </>
      )}

      {suppressions.length > 0 && <section style={{ display: "grid", gap: 9 }}>
        <h3 style={{ margin: 0, fontSize: 17, color: "var(--hp-heading)" }}>Исключённые мероприятия</h3>
        {suppressions.map(item => <article key={item.id} style={{ border: "1px solid var(--hp-border)", borderRadius: 8, padding: 10, display: "grid", gap: 5 }}>
          <strong>{item.title || "Матч лиги"}</strong>
          {item.startTime && <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>{formatTimestamp(item.startTime)}</span>}
          {item.competitionName && <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>{item.competitionName}</span>}
          <button type="button" disabled={busy !== null} onClick={() => void handleRestoreSuppression(item)} style={{ ...secondaryButtonStyle, justifySelf: "start" }}>Вернуть в синхронизацию</button>
        </article>)}
      </section>}

      <details>
        <summary style={{ cursor: "pointer", color: "var(--hp-primary)", fontWeight: 800 }}>+ Добавить команду</summary>
        <form onSubmit={(event) => void handleSearch(event)} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={100} placeholder="Название команды" style={{ ...inputStyle, minWidth: 0, flex: "1 1 180px" }} />
          <button type="submit" disabled={busy !== null} style={buttonStyle}>{busy === "search" ? "Ищем..." : "Найти команду"}</button>
        </form>
        <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
          {results.map((item) => (
            <article key={`${item.provider}:${item.externalTeamId}`} style={{ border: "1px solid var(--hp-border)", borderRadius: 8, padding: 12, display: "grid", gridTemplateColumns: item.logoUrl ? "52px minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 12 }}>
              {item.logoUrl && <img src={item.logoUrl} alt="" width={52} height={52} style={{ objectFit: "contain", maxWidth: "100%" }} />}
              <div style={{ minWidth: 0, display: "grid", gap: 6 }}>
                <strong style={{ color: "var(--hp-heading)", overflowWrap: "anywhere" }}>{item.name}</strong>
                {(item.city || item.country) && <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>{[item.city, item.country].filter(Boolean).join(", ")}</span>}
                {item.divisionName && <span style={{ color: "var(--hp-muted)", fontSize: 13 }}>{item.divisionName}</span>}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {item.profileUrl && <a href={item.profileUrl} target="_blank" rel="noopener noreferrer" style={{ ...secondaryButtonStyle, textAlign: "center", textDecoration: "none", boxSizing: "border-box" }}>Открыть на сайте лиги</a>}
                  <button type="button" onClick={() => void handleAdd(item)} disabled={busy !== null} style={buttonStyle}>Добавить</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </details>
    </section>
  );
}
